export type PromotionRules = {
  allowRealMoneyContribution: boolean;
  contributionFactors: PromotionContributionFactors;
  defaultContributionFactor: number;
  turnover: number;
  turnoverRealMoney: number;
};

export const BonusType = {
  FREE_SPIN: 'FREE_SPIN',
  MONEY: 'MONEY',
} as const;

export type BonusType = (typeof BonusType)[keyof typeof BonusType];

export type Bonuses = {
  actualBonuses: Bonus[] | null;
  pastBonuses: Bonus[] | null;
  allBonuses: Bonus[] | null;
  campaignIdList: string[] | null;
};

export type Bonus = {
  bonusBalance: BonusBalance;
  bonusDeposit: number;
  bonusId: string;
  campaignId: string;
  created: string;
  expires: string;
  modified: string;
  campaign?: Campaign;
  isInProgress: null | boolean;
  priority: number;
  stage: BonusStage;
  state: BonusState;
  tags: string[];
  turnoverTotal: number;
  type: BonusType;
  wageringCounter: number;
  wageringProgressPct: number;
  wageringTarget: number;
  // wageringContribution: boolean; will be provided by BE in the future
};

export type BonusBalance = { money: number; freeSpin?: { count: number; value: number } };

export type IncrementalSubscription = {
  isFreeSpinCampaign: boolean;
  getBonusDetails: (freeSpinsCount?: number) => IncrementalSubscriptionBonusDetails;
  totalSteps: number;
  unlockedSteps: number;
  wageringProgressPct: number;
  currentWager: number;
  remainingWagerForNextStep: number;
  isInProgress: null | boolean;
  deactivationReason?: DeactivationReason;
  deactivatedAt?: Date;
  bonusId?: string;
  campaignId: string;
  created: Date;
  currency: string;
  expiration: Date;
  id: string;
};

export type IncrementalSubscriptionBonusDetails = {
  maxBonus: number;
  perStepBonus: number;
  unlockedBonusAmount: number;
};

export type Subscription = {
  active: boolean;
  campaignId: string;
  created: Date;
  currency: string;
  expiration: Date;
  id: string;
  config?: SubscriptionConfigAPI;
  deactivationReason?: DeactivationReason;
};

type SubscriptionEvent = 'crm:manual' | 'casino:bet' | 'chiptransfer:deposit' | 'activity:bet';

export type SubscriptionConfigAPI = {
  freeSpinCount?: number; // only for free spin incremental bonus campaigns
  activations: number;
  event: SubscriptionEvent;
  matchingPercentage: number;
  repeatable?: boolean;
  max: number;
  min: number;
  reward?: MultitierReward;
  type: 'EVENT' | 'AUTO_ACTIVATE';
  occurrence: 'CUMULATIVE' | 'SINGLE';
};

export type SubscriptionAPI = {
  active: boolean;
  campaignId: string;
  created: string;
  currency: string;
  expiration: string;
  id: string;
  userId: string;
  bonusId?: string;
  activations: number;
  deactivationReason?: DeactivationReason;
  deactivatedAt?: string;
  balance?: {
    cumulated: number;
    threshold: number;
  };
  config: SubscriptionConfigAPI;
};

export type SubscribeResponseDetails = {
  campaignId: string;
  details?: {
    unsubscribed?: {
      campaignId: string;
      subscriptionId: string;
      userId: string;
    };
    subscriptionId: string;
    userId: string;
  };
  errorCode?: number;
  subscribed?: boolean;
};

export type ProviderCampaignConfig = {
  numFreeRounds?: number;
  name: string;
  integration: string;
  games: string[];
  campaignValue?: number;
  totalSpins?: number;
  betAmount: number;
};

export type ExternalConfig = {
  providerCampaign?: ProviderCampaignConfig;
  gamesIncluded?: string[];
};

export type PrivateTags = {
  campaignType: string;
  purpose: string;
  journey: string;
  journeyType: string;
  bonusType: string | null;
  linkedCampaignId: string | null;
  optionalFlags: {
    VIP: boolean;
    EXPERIMENT: boolean;
  };
};

export type Wallet = {
  currency: string;
  id: string;
};

export type CampaignContent = {
  campaignId: string;
  imageBanner: string | null;
  imageThumbnail: string | null;
  info: string | null;
  postOptInCTA: string | null;
  postOptInLink: string | null;
  postOptInText: string | null;
  tac: string | null;
  title: string;
  ibcTitle: string | null;
  subtitle: string | null;
};

export type BECampaign = {
  id: string;
  name: string;
  description: string;
  subscriptionConfig: SubscriptionConfigAPI;
  externalConfig: ExternalConfig;
  expiresInDays: number;
  restriction: PromotionRestrictionType;
  type: 'FREE_SPIN' | 'MONEY';
  validFrom: string;
  validTo: string;
  rules: PromotionRules;
};

export type CampaignType = 'FREE_SPIN' | 'MONEY';

export type CampaignWithoutContent = {
  id: string;
  repeatable: boolean;
  isWaitingForEvent: boolean;
  subscription?: Subscription;
  isEventBased: boolean;
  externalConfig?: ExternalConfig;
  subscriptionConfig?: SubscriptionConfigAPI;
  expiresInDays: number;
  restriction: PromotionRestrictionType;
  type: CampaignType;
  validFrom: Date;
  validTo: Date;
  subscriptionEvent: SubscriptionEvent;
  rules: PromotionRules;
};

export type Campaign = CampaignWithoutContent & { content: CampaignContent };

export type Promotion = {
  id: string;
  priority: number;
  validFrom: Date;
  validTo: Date | null;
  imageThumbnail: string | null;
  imageBanner: string | null;
  postOptInLink: string | null;
  title: string;
  subtitle: string;
  postOptInCTA: string;
  description: string;
};

export const PromotionRestrictionType = {
  PRIVATE: 'PRIVATE',
  PUBLIC: 'PUBLIC',
  WELCOME: 'WELCOME',
} as const;

export type PromotionRestrictionType = (typeof PromotionRestrictionType)[keyof typeof PromotionRestrictionType];

export type PromotionContributionFactors = {
  blackjack: number;
  live_blackjack: number;
  live_other: number;
  live_roulette: number;
  poker: number;
  roulette: number;
  slots: number;
  tablegame: number;
};

export type PromotionAction = {
  action: PromotionActionType;
  url?: string;
};

export const PromotionActionType = {
  FREE_SPIN: 'FREE_SPIN',
  MONEY: 'MONEY',
} as const;

export type PromotionActionType = (typeof PromotionActionType)[keyof typeof PromotionActionType];

export const CampaignSubscriptionType = {
  AUTO_ACTIVATE: 'AUTO_ACTIVATE',
  EVENT: 'EVENT',
} as const;

export type CampaignSubscriptionType = (typeof CampaignSubscriptionType)[keyof typeof CampaignSubscriptionType];

export type MultitierReward = {
  name: string;
  tiers: {
    reward: {
      action: string;
      amount: number;
      campaignId: string;
      count: number;
    };
    threshold: number;
  }[];
};

export type Reward = {
  amount?: number;
  creditedOn: string;
  expirationDate: string;
  status: string;
  studioID: string;
  title: string;
};

export const BonusState = {
  ACTIVE: 'ACTIVE',
  CANCELED: 'CANCELED',
  EXPIRED: 'EXPIRED',
  FULFILLED: 'FULFILLED',
} as const;

export type BonusState = (typeof BonusState)[keyof typeof BonusState];

export const BonusStage = {
  BONUS_ACTIVE: 'BONUS_ACTIVE',
  BONUS_CANCELED: 'BONUS_CANCELED',
  BONUS_EXPIRED: 'BONUS_EXPIRED',
  BONUS_FULFILLED: 'BONUS_FULFILLED',
  FREE_SPIN_ACTIVE: 'FS_ACTIVE',
  FREE_SPIN_CANCELED: 'FS_CANCELED',
  FREE_SPIN_CONSUMED: 'FS_CONSUMED',
  FREE_SPIN_EXPIRED: 'FS_EXPIRED',
} as const;

export type BonusStage = (typeof BonusStage)[keyof typeof BonusStage];

export const BonusActionType = {
  FORFEIT_REWARD: 'forfeit-reward',
  VIEW_TAC: 'view-tac',
} as const;

export type BonusActionType = (typeof BonusActionType)[keyof typeof BonusActionType];

export const BonusPageView = {
  BONUS_GAMES: 'bonus-spiele',
  DEPOSIT_BONUS: 'einzahlungsbonus',
  OFFER_PAGE: 'offer-page',
  // TODO: check if we could avoid using empty string here
  OFFER_LIST_PAGE: '',
  REWARDS: 'meine-boni',
  PAST_REWARDS: 'vergangen-boni',
} as const;

export type BonusPageView = (typeof BonusPageView)[keyof typeof BonusPageView];

export const BonusModuleEventTypes = {
  BONUS_EVENT_SUBSCRIBED: 'bonus-event-subscribed',
  CANCEL_BONUS: 'delete-reward',
  CANCEL_SUBSCRIPTION: 'delete-promotion',
  CLAIM_WELCOME: 'claim-welcome',
  GET_ACTIVATED_CAMPAIGNS: 'get-activated-campaigns',
  GET_AVAILABLE_CAMPAIGNS: 'get-available-campaigns',
  GET_ELIGIBLE_GAMES: 'get-eligible-games',
  LOAD_MORE_REWARDS: 'load-more-rewards',
  SET_VIEW: 'set-view',
  SUBSCRIBE: 'subscribe',
  SUBSCRIBE_ERROR: 'subscribe_error',
  UNSUBSCRIBE: 'unsubscribe',
  UNSUBSCRIBE_MODAL: 'unsubscribe_modal',
} as const;

export type BonusModuleEventTypes = (typeof BonusModuleEventTypes)[keyof typeof BonusModuleEventTypes];

export const DeactivationReason = {
  LIMIT_REACHED: 'LIMIT_REACHED',
  BONUS_CLAIMED: 'BONUS_CLAIMED',
  EXPIRED: 'EXPIRED',
  UNSUBSCRIBED: 'UNSUBSCRIBED',
} as const;

export type DeactivationReason = (typeof DeactivationReason)[keyof typeof DeactivationReason];

export type PromotionView = {
  id: string;
  tac?: SubPage;
  view: BonusPageView;
};

export type SubscriptionDetails = {
  campaign: Campaign;
  hadConflicts?: boolean;
  errorCode?: number;
  subscribed?: boolean;
};

export type EligibleGame = { gameId: string; integration?: string };

export const RestrictionsType = {
  DEPOSIT: 'deposit',
} as const;

export type RestrictionsType = (typeof RestrictionsType)[keyof typeof RestrictionsType];

export type BonusEventDetail =
  | { type: typeof BonusModuleEventTypes.BONUS_EVENT_SUBSCRIBED; payload: boolean }
  | { type: typeof BonusModuleEventTypes.CLAIM_WELCOME; payload: SubscriptionDetails }
  | { type: typeof BonusModuleEventTypes.CANCEL_SUBSCRIPTION; payload: string }
  | { type: typeof BonusModuleEventTypes.CANCEL_BONUS; payload: string }
  | { type: typeof BonusModuleEventTypes.GET_AVAILABLE_CAMPAIGNS; payload: string }
  | { type: typeof BonusModuleEventTypes.GET_ACTIVATED_CAMPAIGNS; payload: string }
  | { type: typeof BonusModuleEventTypes.GET_ELIGIBLE_GAMES; payload: string }
  | { type: typeof BonusModuleEventTypes.LOAD_MORE_REWARDS; payload: { limit: number; skip: number } }
  | { type: typeof BonusModuleEventTypes.SET_VIEW; payload: BonusPageView | PromotionView }
  | { type: typeof BonusModuleEventTypes.SUBSCRIBE_ERROR; payload: boolean }
  | { type: typeof BonusModuleEventTypes.SUBSCRIBE; payload: SubscriptionDetails }
  | { type: typeof BonusModuleEventTypes.UNSUBSCRIBE_MODAL; payload: string }
  | { type: typeof BonusModuleEventTypes.UNSUBSCRIBE; payload: UnsubscribeDetails };

export const BONUS_MODULE_EVENT = 'bonus-module-event';

export interface Types extends CustomEvent {
  detail: BonusEventDetail;
}

export type UnsubscribeDetails = {
  id: string;
  type: 'bonus' | 'subscription';
};

export const SubPage = {
  INFO: 'info',
  TAC: 'tac',
} as const;

export type SubPage = (typeof SubPage)[keyof typeof SubPage];
